import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  useTheme,
  Alert,
  Container,
  Paper,
  CircularProgress,
  Skeleton
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import AWSAccountSetup from '../components/AWSAccountSetup';

/* Outline SVG icons */
const ShieldIcon = () => (
  <svg width="22" height="22" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M10 2L3 5v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V5l-7-3z" strokeLinejoin="round" />
  </svg>
);
const DollarIcon = () => (
  <svg width="22" height="22" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 14l4-4 3 3 7-7" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M13 6h4v4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const CloudIcon = () => (
  <svg width="22" height="22" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M5.5 15A4.5 4.5 0 015 6.1a6 6 0 0111.5 1.4A3.5 3.5 0 0115.5 15h-10z" strokeLinejoin="round" />
  </svg>
);
const CheckIcon = () => (
  <svg width="22" height="22" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="10" cy="10" r="7" />
    <path d="M7 10l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 8h10M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function Dashboard() {
  const { user } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Real data states
  const [awsAccounts, setAwsAccounts] = useState([]);
  const [securityScore, setSecurityScore] = useState(null);
  const [monthlySavings, setMonthlySavings] = useState(null);
  const [complianceScore, setComplianceScore] = useState(null);
  const [securityScanDone, setSecurityScanDone] = useState(false);
  const [costAnalysisDone, setCostAnalysisDone] = useState(false);
  const [complianceDone, setComplianceDone] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load all data in parallel
      const [accountsRes, evidenceRes] = await Promise.allSettled([
        axios.get('/api/aws/accounts'),
        axios.get('/api/regulatory-bot/evidence/history')
      ]);

      // AWS Accounts
      const accounts = accountsRes.status === 'fulfilled' 
        ? (accountsRes.value.data.accounts || []) 
        : [];
      setAwsAccounts(accounts);

      // If we have accounts, load security and cost data
      if (accounts.length > 0) {
        const accountId = accounts[0].id;

        const [securityRes, costRes] = await Promise.allSettled([
          axios.get(`/api/security/assessments/${accountId}`),
          axios.get(`/api/cost/analyses/${accountId}`)
        ]);

        // Security Score - from latest assessment
        if (securityRes.status === 'fulfilled') {
          const assessments = securityRes.value.data.assessments || [];
          if (assessments.length > 0) {
            const latest = assessments[0];
            setSecurityScore(latest.risk_score || latest.riskScore || null);
            setSecurityScanDone(true);
          }
        }

        // Cost Analysis - monthly savings
        if (costRes.status === 'fulfilled') {
          const analyses = costRes.value.data.analyses || [];
          if (analyses.length > 0) {
            const latest = analyses[0];
            const savings = latest.potential_savings || latest.potentialSavings || 0;
            setMonthlySavings(savings);
            setCostAnalysisDone(true);
          }
        }
      }

      // Compliance - from regulatory bot evidence history
      if (evidenceRes.status === 'fulfilled') {
        const history = evidenceRes.value.data.history || [];
        if (history.length > 0) {
          // Get the latest compliance score
          const latest = history[0];
          setComplianceScore(latest.complianceScore || null);
          setComplianceDone(true);
        }
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const hasAWSAccount = awsAccounts.length > 0;

  const features = [
    {
      title: 'CSPM Security',
      description: 'Security posture analysis with enterprise-grade scanning and remediation guidance',
      icon: <ShieldIcon />,
      color: '#2A6BDB',
      bgColor: '#EFF5FF',
      path: '/security',
      badge: 'AI-Powered',
    },
    {
      title: 'FinOps',
      description: 'Spending analysis with recommendations for cost optimization',
      icon: <DollarIcon />,
      color: '#1E4FA0',
      bgColor: '#E8EEF9',
      path: '/cost-optimization',
      badge: 'Analytics',
    },
    {
      title: 'Compliance',
      description: 'Compliance monitoring for GDPR, HIPAA, PCI-DSS, SOC2, and DPDP',
      icon: <CheckIcon />,
      color: '#3D7BE0',
      bgColor: '#E3EDFC',
      path: '/compliance',
      badge: 'Multi-Framework',
    },
  ];

  const stats = [
    { 
      title: 'AWS Accounts', 
      value: loading ? null : String(awsAccounts.length), 
      icon: <CloudIcon />, 
      color: '#2A6BDB', 
      bg: '#EFF5FF' 
    },
    { 
      title: 'Security Score', 
      value: loading ? null : (securityScore !== null ? `${securityScore}%` : '-'), 
      icon: <ShieldIcon />, 
      color: '#1E4FA0', 
      bg: '#E8EEF9' 
    },
    { 
      title: 'Monthly Savings', 
      value: loading ? null : (monthlySavings !== null ? `$${Number(monthlySavings).toLocaleString()}` : '$0'), 
      icon: <DollarIcon />, 
      color: '#3D7BE0', 
      bg: '#E3EDFC' 
    },
    { 
      title: 'Compliance', 
      value: loading ? null : (complianceScore !== null ? `${complianceScore}%` : '-'), 
      icon: <CheckIcon />, 
      color: '#5B9AFF', 
      bg: '#EFF5FF' 
    },
  ];

  const quickStartSteps = [
    { step: '1', title: 'Connect AWS', desc: 'Secure read-only integration', done: hasAWSAccount, path: '/aws-accounts' },
    { step: '2', title: 'Security Scan', desc: 'Run CSPM assessment', done: securityScanDone, path: '/security' },
    { step: '3', title: 'Cost Analysis', desc: 'Find savings opportunities', done: costAnalysisDone, path: '/cost-optimization' },
    { step: '4', title: 'Compliance', desc: 'Run compliance assessment', done: complianceDone, path: '/regulatory-bot' },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 0 }}>
      {/* Welcome */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
          Welcome back{user?.company_name ? `, ${user.company_name}` : ''}
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
          Your AWS automation platform overview
        </Typography>
      </Box>

      {/* AWS Setup Alert */}
      {!loading && !hasAWSAccount && (
        <Alert
          severity="info"
          sx={{ mb: 3, borderRadius: '8px' }}
          action={
            <Button size="small" variant="contained" onClick={() => navigate('/aws-accounts')}>
              Connect AWS
            </Button>
          }
        >
          Connect your AWS account to unlock all platform features.
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {stats.map((stat, i) => (
          <Grid item xs={6} lg={3} key={i}>
            <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '8px',
                      bgcolor: theme.palette.mode === 'light' ? stat.bg : 'grey.300',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: stat.color,
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', fontWeight: 500 }}>
                    {stat.title}
                  </Typography>
                </Box>
                {stat.value === null ? (
                  <Skeleton width={60} height={40} />
                ) : (
                  <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: 'text.primary' }}>
                    {stat.value}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Features */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.primary', mb: 2 }}>
          Core Features
        </Typography>
        <Grid container spacing={2}>
          {features.map((feature, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card
                elevation={0}
                onClick={() => navigate(feature.path)}
                sx={{
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '10px',
                        bgcolor: theme.palette.mode === 'light' ? feature.bgColor : 'grey.300',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: feature.color,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Chip
                      label={feature.badge}
                      size="small"
                      sx={{
                        fontSize: '0.6875rem',
                        height: 22,
                        bgcolor: theme.palette.mode === 'light' ? feature.bgColor : 'grey.300',
                        color: feature.color,
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                    {feature.title}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', lineHeight: 1.5, mb: 1.5 }}>
                    {feature.description}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }}>
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>Open</Typography>
                    <ArrowIcon />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Quick Start */}
      <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.primary', mb: 2 }}>
            Quick Start
          </Typography>
          <Grid container spacing={2}>
            {quickStartSteps.map((item, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Paper
                  elevation={0}
                  onClick={() => navigate(item.path)}
                  sx={{
                    p: 2,
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: item.done ? 'success.main' : 'divider',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    '&:hover': { borderColor: 'primary.main' },
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: item.done ? 'success.main' : 'primary.main',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      mb: 1,
                    }}
                  >
                    {item.done ? '✓' : item.step}
                  </Box>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', mb: 0.25 }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {item.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <AWSAccountSetup
        open={setupDialogOpen}
        onClose={() => setSetupDialogOpen(false)}
        onSetupLater={() => setSetupDialogOpen(false)}
      />
    </Container>
  );
}

export default Dashboard;
